import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";

import {
  classeController,
  domainController,
  functionController,
  gradeController,
  montantController,
  schoolYearController,
  terminalController,
} from "../../_api/controllers/scolarite.controller.ts";
import moment from "moment";
import * as Yup from "yup";

const schoolYearSchema = Yup.object()
  .shape({
    schoolYear: Yup.string()
      .required("L’année scolaire est obligatoire.")
      .matches(/^\d{4}-\d{4}$/, "Format invalide. Exemple: 2025-2026")
      .test(
        "valid-range",
        "Les années doivent se suivre (ex: 2025-2026)",
        (value) => {
          if (!value) return false;
          const [start, end] = value.split("-").map(Number);
          return end === start + 1;
        }
      ),

    startDate: Yup.date()
      .typeError("La date de début est invalide")
      .required("La date de début est obligatoire"),

    endDate: Yup.date()
      .typeError("La date de fin est invalide")
      .required("La date de fin est obligatoire")
      .min(
        Yup.ref("startDate"),
        "La date de fin doit être postérieure à la date de début"
      ),
  })
  // Custom validation for date inclusion inside schoolYear
  .test(
    "dates-in-school-year",
    "Les dates doivent être comprises dans l’année scolaire sélectionnée",
    (values) => {
      const { schoolYear, startDate, endDate } = values || {};
      if (!schoolYear || !startDate || !endDate) return true; // skip if incomplete

      const [startYear, endYear] = schoolYear.split("-").map(Number);
      const schoolStart = new Date(`${startYear}-01-01`);
      const schoolEnd = new Date(`${endYear}-12-31`);

      const start = new Date(startDate);
      const end = new Date(endDate);

      return start >= schoolStart && end <= schoolEnd;
    }
  );

const Scolarite: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<null | string>(null);
  const [notification, setNotification] = useState<any>(null); // { message, type }
  const [searchQuery, setSearchQuery] = useState("");

  //filter for montant (Frais)
  const [filterMontantByClasse, setFilterMontantByClasse] =
    useState("Toutes les classes");
  const [isMontantClassDropdownOpen, setIsMontantClassDropdownOpen] =
    useState(false);
  const [filterMontantSy, setFilterMontantBySy] = useState("Toutes");
  const [isMontantSyDropdownOpen, setIsMontantSyDropdownOpen] = useState(false);

  //filter for classe
  const [filtrerClasseByYear, setfiltrerClasseByYear] =
    useState("Toutes les années");
  const [isYearClassDropdownOpen, setIsYearClassDropdownOpen] = useState(false);

  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  //handleling delete & activate & desactivate items
  interface ActionState {
    actionSection: string;
    actionType: string;
    actionText: string;
    currentId: any;
  }

  interface Action {
    type: "DELETE" | "DESACTIVATE" | "CANCEL" | "ACTIVATE";
    payload: { actionText: string; actionSection: string; currentId: any };
  }
  const ACTION_TYPES = {
    DELETE: "DELETE",
    DESACTIVATE: "DESACTIVATE",
    ACTIVATE: "ACTIVATE",
    CANCEL: "CANCEL",
  };

  const INITIAL_STATE: ActionState = {
    actionSection: "",
    actionType: "",
    actionText: "",
    currentId: null,
  };

  const actionReducer = (state: ActionState, action: Action): ActionState => {
    switch (action.type) {
      case ACTION_TYPES.DELETE:
        return {
          ...state,
          actionType: "delete",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.ACTIVATE:
        return {
          ...state,
          actionType: "activate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.DESACTIVATE:
        return {
          ...state,
          actionType: "desactivate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.CANCEL:
        return {
          actionSection: "",
          actionType: "",
          actionText: "",
          currentId: null,
        };

      default:
        return state;
    }
  };

  const [state_actions, dispatch] = React.useReducer(
    actionReducer,
    INITIAL_STATE
  );

  const [newSchoolYear, setNewSchoolYear] = useState({
    title: "",
    startDate: "",
    endDate: "",
    isActive: false,
  });

  const [editSchoolYearData, setEditSchoolYearData] = useState<{
    id?: any;
    title: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
    type: string;
  }>({
    id: "",
    title: "",
    startDate: "",
    endDate: "",
    isActive: false,
    type: "",
  });

  //class
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showEditClassModal, setShowEditClassModal] = useState(false);
  const [newClass, setNewClass] = useState({
    name: "",
    code: "",
    level: "",
    schoolYear: "",
  });
  const [editClassData, setEditClassData] = useState<{
    id: any;
    name: string;
    code: string;
    level: string;
    schoolYear: string;
  }>({
    id: "",
    name: "",
    code: "",
    level: "",
    schoolYear: "",
  });

  //Domaine
  const [showAddDomaineModal, setShowAddDomaineModal] = useState(false);
  const [showEditDomaineModal, setShowEditDomaineModal] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [newDomain, setNewDomain] = useState({
    designation: "",
    description: "",
  });
  const [editDomainData, setEditDomainData] = useState<{
    id: any;
    designation: string;
    description: string;
  }>({
    id: null,
    designation: "",
    description: "",
  });

  //function
  const [showAddFunctionModal, setShowAddFunctionModal] = useState(false);
  const [showEditFunctionModal, setShowEditFunctionModal] = useState(false);
  const [charCountFunction, setCharCountFunction] = useState(0);
  const [newFunction, setNewFunction] = useState({
    designation: "",
    description: "",
  });
  const [editFunctionData, setEditFunctionData] = useState<{
    id: any;
    designation: string;
    description: string;
  }>({
    id: null,
    designation: "",
    description: "",
  });

  //grade
  const [showAddGradeModal, setShowAddGradeModal] = useState(false);
  const [showEditGradeModal, setShowEditGradeModal] = useState(false);
  const [charCountGrade, setCharCountGrade] = useState(0);
  const [newGrade, setNewGrade] = useState({
    designation: "",
    description: "",
  });
  const [editGradeData, setEditGradeData] = useState<{
    id: any;
    designation: string;
    description: string;
  }>({
    id: null,
    designation: "",
    description: "",
  });

  //montant
  const [showAddMontantModal, setShowAddMontantModal] = useState(false);
  const [showEditMontantModal, setShowEditMontantModal] = useState(false);
  const [newMontant, setNewMontant] = useState({
    montant: "",
    classe_id: "",
    annee_scolaire_id: "",
  });
  const [editMontantData, setEditMontantData] = useState<{
    id: any;
    montant: any;
    classDesignation?: any;
    classe_id: any;
    annee_scolaire_id: any;
  }>({
    id: "",
    montant: "",
    classe_id: "",
    annee_scolaire_id: "",
  });

  //Terminaux
  const [showAddTerminalModal, setShowAddTerminalModal] = useState(false);
  const [showEditTerminalModal, setShowEditTerminalModal] = useState(false);
  const [newTerminal, setNewTerminal] = useState<{
    uid: string;
    designation: string;
  }>({
    uid: "",
    designation: "",
  });
  const [editTerminalData, setEditTerminalData] = useState<{
    id: any;
    uid: string;
    designation: string;
  }>({
    id: "",
    uid: "",
    designation: "",
  });

  const [activeScolariteAccordion, setActiveScolariteAccordion] = useState<
    string | null
  >(null);

  const toggleScolariteAccordion = (section: string) => {
    if (activeScolariteAccordion === section) {
      setActiveScolariteAccordion(null);
    } else {
      setActiveScolariteAccordion(section);
    }
  };

  const toggleYearClassDropdown = () => {
    setIsYearClassDropdownOpen(!isYearClassDropdownOpen);
  };
  const toggleMontantClassDropdown = () => {
    setIsMontantClassDropdownOpen(!isMontantClassDropdownOpen);
  };
  const toggleMontantSyDropdown = () => {
    setIsMontantSyDropdownOpen(!isMontantSyDropdownOpen);
  };

  // Fermeture des menus lors d'un clic à l'extérieur
  const montantClassDropdownRef = React.useRef<HTMLDivElement>(null);
  const montantSyDropdownRef = React.useRef<HTMLDivElement>(null);
  const yearClassDropdownRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //status button
      if (
        yearClassDropdownRef.current &&
        !yearClassDropdownRef.current.contains(event.target as Node)
      ) {
        setIsYearClassDropdownOpen(false);
      }

      if (
        montantClassDropdownRef.current &&
        !montantClassDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMontantClassDropdownOpen(false);
      }

      if (
        montantSyDropdownRef.current &&
        !montantSyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMontantSyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const queryClient = useQueryClient();

  //fetch anne scolaire
  const { data: schoolYearData } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });
  //fetch all classes
  const { data: classeData } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });
  //fetch all domains
  const { data: domainData } = useQuery({
    queryKey: ["domains"],
    queryFn: domainController.getAll,
  });

  //fetch all functions
  const { data: functionData } = useQuery({
    queryKey: ["functions"],
    queryFn: functionController.getAll,
  });
  //fetch all grade
  const { data: gradeData } = useQuery({
    queryKey: ["grades"],
    queryFn: gradeController.getAll,
  });
  //fetch all montant
  const { data: montantData } = useQuery({
    queryKey: ["montants"],
    queryFn: montantController.getAll,
  });
  //fetch all ontant
  const { data: terminalData } = useQuery({
    queryKey: ["terminaux"],
    queryFn: terminalController.getAll,
  });

  const schoolYearArr = schoolYearData?.data || [];
  const classeDataArr = classeData?.data || [];
  const domainDataArr = domainData?.data || [];
  const functionDataArr = functionData?.data || [];
  const gradeDataArr = gradeData?.data || [];
  const montantDataArr = montantData?.data || [];
  const terminalDataArr = terminalData?.data || [];

  //get all years for classes date created at
  const years = [
    ...new Set(
      classeDataArr.map((item) => moment(item.created_at).format("YYYY"))
    ),
  ];

  //get active school year
  const activeYear = schoolYearArr.filter((item) => item.status)[0]
    ?.designation;

  const activeYearId = schoolYearArr.filter((item) => item.status)[0]?.id;

  //get Complete montant per class array
  const completeMontantArray = montantDataArr.map((montant) => {
    let classeDesignation: any, anneeScolaireDesignation: any;
    for (let classe of classeDataArr) {
      if (classe.id === montant.classe_id) {
        classeDesignation = classe.designation;
      }
    }
    for (let sy of schoolYearArr) {
      if (sy.id === montant.annee_scolaire_id) {
        anneeScolaireDesignation = sy.designation;
      }
    }
    return {
      ...montant,
      classeDesignation,
      anneeScolaireDesignation,
    };
  });

  //filter class
  const filteredClasses = classeDataArr.filter((classe) => {
    const matchesSearch =
      classe.designation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      classe.code.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesYear =
      filtrerClasseByYear === "Toutes les années" ||
      classe.created_at.includes(filtrerClasseByYear);

    return matchesSearch && matchesYear;
  });

  //filter montant
  const filteredMontants = completeMontantArray.filter((montant) => {
    const matchesClass =
      filterMontantByClasse === "Toutes les classes" ||
      montant.classeDesignation.toLowerCase() ===
        filterMontantByClasse.toLowerCase();

    const matchesSy =
      filterMontantSy === "Toutes" ||
      filterMontantSy === montant.anneeScolaireDesignation;
    return matchesClass && matchesSy;
  });

  const mutationCreateShoolYear = useMutation({
    mutationFn: () =>
      schoolYearController.create({
        designation: newSchoolYear.title,
        date_debut: newSchoolYear.startDate,
        date_fin: newSchoolYear.endDate,
      }),

    onSuccess: (data) => {
      setShowAddYearModal(false);
      showNotification("Enregistré avec succès", "success");
      setNewSchoolYear({
        title: "",
        startDate: "",
        endDate: "",
        isActive: false,
      });
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateShoolYear = useMutation({
    mutationFn: () =>
      schoolYearController.update(editSchoolYearData.id, {
        designation: editSchoolYearData.title,
        date_debut: editSchoolYearData.startDate,
        date_fin: editSchoolYearData.endDate,
      }),

    onSuccess: (data) => {
      setShowEditModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditSchoolYearData({
        id: "",
        title: "",
        startDate: "",
        endDate: "",
        isActive: false,
        type: "",
      });
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateClass = useMutation({
    mutationFn: () =>
      classeController.create({
        designation: newClass.name,
        code: newClass.code,
      }),

    onSuccess: () => {
      setShowAddClassModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewClass({
        name: "",
        code: "",
        level: "",
        schoolYear: "",
      });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateClass = useMutation({
    mutationFn: () =>
      classeController.update(editClassData.id, {
        designation: editClassData.name,
        code: editClassData.code,
      }),

    onSuccess: (data) => {
      setShowEditClassModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditClassData({
        id: null,
        name: "",
        code: "",
        level: "",
        schoolYear: "",
      });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateDomain = useMutation({
    mutationFn: () =>
      domainController.create({
        designation: newDomain.designation,
        code: newDomain.description,
      }),

    onSuccess: () => {
      setShowAddDomaineModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewDomain({
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateDomain = useMutation({
    mutationFn: () =>
      domainController.update(editDomainData.id, {
        designation: editDomainData.designation,
        code: editDomainData.description,
      }),

    onSuccess: () => {
      setShowEditDomaineModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditDomainData({
        id: null,
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateFunction = useMutation({
    mutationFn: () =>
      functionController.create({
        designation: newFunction.designation,
        code: newFunction.description,
      }),

    onSuccess: () => {
      setShowAddFunctionModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewFunction({
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["functions"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateFunction = useMutation({
    mutationFn: () =>
      functionController.update(editFunctionData.id, {
        designation: editFunctionData.designation,
        code: editFunctionData.description,
      }),

    onSuccess: () => {
      setShowEditFunctionModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditFunctionData({
        id: null,
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["functions"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateGrade = useMutation({
    mutationFn: () =>
      gradeController.create({
        designation: newGrade.designation,
        code: newGrade.description,
      }),

    onSuccess: () => {
      setShowAddGradeModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewGrade({
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["grades"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateGrade = useMutation({
    mutationFn: () =>
      gradeController.update(editGradeData.id, {
        designation: editGradeData.designation,
        code: editGradeData.description,
      }),

    onSuccess: () => {
      setShowEditGradeModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditGradeData({
        id: null,
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["grades"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateMontant = useMutation({
    mutationFn: () =>
      montantController.create({
        montant: newMontant.montant,
        classe_id: newMontant.classe_id,
        annee_scolaire_id: activeYearId,
      }),

    onSuccess: (data) => {
      setShowAddMontantModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewMontant({
        montant: "",
        classe_id: "",
        annee_scolaire_id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["montants"] });
    },

    onError: (err) => {
      console.log("======ERRR======");
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateMontant = useMutation({
    mutationFn: () =>
      montantController.update(editMontantData.id, {
        montant: editMontantData.montant,
        classe_id: editMontantData.classe_id,
        annee_scolaire_id: editMontantData.annee_scolaire_id,
      }),

    onSuccess: () => {
      setShowEditMontantModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditMontantData({
        id: "",
        montant: "",
        classe_id: "",
        annee_scolaire_id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["montants"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateTerminal = useMutation({
    mutationFn: () =>
      terminalController.create({
        uid: newTerminal.uid,
        designation: newTerminal.designation,
      }),

    onSuccess: (data) => {
      setShowAddTerminalModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewTerminal({
        uid: "",
        designation: "",
      });
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateTerminal = useMutation({
    mutationFn: () =>
      terminalController.update(editTerminalData.id, {
        uid: editTerminalData.uid,
        designation: editTerminalData.designation,
      }),

    onSuccess: () => {
      setShowEditTerminalModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditTerminalData({
        id: "",
        uid: "",
        designation: "",
      });
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteShoolYear = useMutation({
    mutationFn: () => schoolYearController.delete(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });

      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteClass = useMutation({
    mutationFn: () => classeController.delete(state_actions.currentId),

    onSuccess: (data) => {
      console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteDomain = useMutation({
    mutationFn: () => domainController.delete(state_actions.currentId),

    onSuccess: (data) => {
      console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteMontant = useMutation({
    mutationFn: () => montantController.delete(state_actions.currentId),

    onSuccess: (data) => {
      console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["montants"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteTerminal = useMutation({
    mutationFn: () => terminalController.delete(state_actions.currentId),

    onSuccess: (data) => {
      console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivateShoolYear = useMutation({
    mutationFn: () => schoolYearController.activate(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivateClass = useMutation({
    mutationFn: () => classeController.activate(state_actions.currentId),

    onSuccess: (data) => {
      console.log(data);
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDesactivateClass = useMutation({
    mutationFn: () => classeController.desactivate(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleEditClass = () => {
    if (!editClassData.name.trim() || !editClassData.code.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationUpdateClass.mutate();
  };
  const handleSaveNewClass = () => {
    if (!newClass.name.trim() || !newClass.code.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationCreateClass.mutate();
  };
  const handleEditDomain = () => {
    if (
      !editDomainData.designation.trim() ||
      !editDomainData.description.trim()
    )
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationUpdateDomain.mutate();
  };
  const handleSaveNewDomain = () => {
    if (!newDomain.designation.trim() || !newDomain.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationCreateDomain.mutate();
  };
  const handleEditFunction = () => {
    if (
      !editFunctionData.designation.trim() ||
      !editFunctionData.description.trim()
    )
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationUpdateFunction.mutate();
  };
  const handleSaveNewFunction = () => {
    if (!newFunction.designation.trim() || !newFunction.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationCreateFunction.mutate();
  };
  const handleEditGrade = () => {
    if (!editGradeData.designation.trim() || !editGradeData.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationUpdateGrade.mutate();
  };
  const handleSaveNewGrade = () => {
    if (!newGrade.designation.trim() || !newGrade.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationCreateGrade.mutate();
  };
  const handleEditMontant = () => {
    if (!editMontantData.montant || !editMontantData.classe_id)
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationUpdateMontant.mutate();
  };
  const handleSaveNewMontant = () => {
    if (!newMontant.montant || !newMontant.classe_id)
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationCreateMontant.mutate();
  };

  const handleEditTerminal = () => {
    if (!editTerminalData.uid.trim() || !editTerminalData.designation.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationUpdateTerminal.mutate();
  };

  const handleSaveNewTerminal = () => {
    if (!newTerminal.uid.trim() || !newTerminal.designation.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur"
      );
    mutationCreateTerminal.mutate();
  };

  const handleEditYear = async () => {
    try {
      await schoolYearSchema.validate({
        schoolYear: editSchoolYearData.title,
        startDate: editSchoolYearData.startDate,
        endDate: editSchoolYearData.endDate,
      });
      mutationUpdateShoolYear.mutate();
    } catch (err: any) {
      showNotification(err.message, "error");
    }
  };

  const handleSaveNewYear = async () => {
    try {
      await schoolYearSchema.validate({
        schoolYear: newSchoolYear.title,
        startDate: newSchoolYear.startDate,
        endDate: newSchoolYear.endDate,
      });
      mutationCreateShoolYear.mutate();
    } catch (err: any) {
      showNotification(err.message, "error");
    }
  };
  const handleDelete = () => {
    if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "schoolYear"
    ) {
      mutationDeleteShoolYear.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "class"
    ) {
      mutationDeleteClass.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "domain"
    ) {
      mutationDeleteDomain.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "montant"
    ) {
      mutationDeleteMontant.mutate();
    }
  };

  const handleActivate = () => {
    if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "schoolYear"
    ) {
      mutationActivateShoolYear.mutate();
    } else if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "class"
    ) {
      mutationActivateClass.mutate();
    }
  };
  const handleDesactivate = () => {
    if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "class"
    ) {
      mutationDesactivateClass.mutate();
    }
  };

  const handleResetFilter = () => {
    setFilterMontantBySy("Toutes");
    setFilterMontantByClasse("Toutes les classes");
  };

  return (
    <div className="space-y-6">
      {/* Année scolaire et classe */}
      <div
        className={`rounded-lg overflow-hidden border ${
          isDarkMode ? "border-gray-700" : "border-gray-300"
        }`}
      >
        <div
          className={`p-4 flex justify-between items-center cursor-pointer ${
            isDarkMode ? "bg-[#161B22]" : "bg-gray-50"
          }`}
          onClick={() => toggleScolariteAccordion("anneeScolaire")}
        >
          <div className="flex items-center">
            <i className="fas fa-calendar-alt text-blue-500 mr-3"></i>
            <h3 className="text-lg font-medium">Année scolaire et classe</h3>
          </div>
          <i
            className={`fas ${
              activeScolariteAccordion === "anneeScolaire"
                ? "fa-chevron-up"
                : "fa-chevron-down"
            } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          ></i>
        </div>
        {activeScolariteAccordion === "anneeScolaire" && (
          <div className={`p-5 ${isDarkMode ? "bg-[#0D1117]" : "bg-white"}`}>
            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Années scolaires</h4>
              <button
                id="addSchoolYearBtn"
                onClick={() => {
                  setShowAddYearModal(true);
                }}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter une année
              </button>
              {/* Add School Year Modal */}
              {(showAddYearModal || showEditModal) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div
                    className={`${
                      isDarkMode ? "bg-[#1c2128]" : "bg-white"
                    } rounded-lg shadow-xl w-full max-w-md mx-4`}
                  >
                    <div
                      className={`px-6 py-4 border-b ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <h3 className="text-xl font-medium">
                        {showEditModal
                          ? "Modifier une année scolaire"
                          : "Ajouter une année scolaire"}
                      </h3>
                    </div>
                    <div className="px-6 py-4">
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Intitulé de l'année
                        </label>
                        <input
                          name="schoolYear"
                          type="text"
                          value={
                            showEditModal
                              ? editSchoolYearData.title
                              : newSchoolYear.title
                          }
                          onChange={(e) => {
                            showEditModal
                              ? setEditSchoolYearData({
                                  ...editSchoolYearData,
                                  title: e.target.value,
                                })
                              : setNewSchoolYear({
                                  ...newSchoolYear,
                                  title: e.target.value,
                                });
                          }}
                          placeholder="2026-2027"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Date de début
                        </label>
                        <input
                          name="startDate"
                          type="date"
                          value={
                            showEditModal
                              ? editSchoolYearData.startDate
                              : newSchoolYear.startDate
                          }
                          onChange={(e) =>
                            showEditModal
                              ? setEditSchoolYearData({
                                  ...editSchoolYearData,
                                  startDate: e.target.value,
                                })
                              : setNewSchoolYear({
                                  ...newSchoolYear,
                                  startDate: e.target.value,
                                })
                          }
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Date de fin
                        </label>
                        <input
                          name="endDate"
                          type="date"
                          value={
                            showEditModal
                              ? editSchoolYearData.endDate
                              : newSchoolYear.endDate
                          }
                          onChange={(e) =>
                            showEditModal
                              ? setEditSchoolYearData({
                                  ...editSchoolYearData,
                                  endDate: e.target.value,
                                })
                              : setNewSchoolYear({
                                  ...newSchoolYear,
                                  endDate: e.target.value,
                                })
                          }
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                      {showEditModal && (
                        <div className="mb-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={
                                showEditModal
                                  ? editSchoolYearData.isActive
                                  : newSchoolYear.isActive
                              }
                              onChange={(e) =>
                                showEditModal
                                  ? setEditSchoolYearData({
                                      ...editSchoolYearData,
                                      isActive: e.target.checked,
                                    })
                                  : setNewSchoolYear({
                                      ...newSchoolYear,
                                      isActive: e.target.checked,
                                    })
                              }
                              className="form-checkbox"
                            />

                            <span
                              className={`ml-2 ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Année active
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        onClick={() => {
                          if (showEditModal) {
                            setShowEditModal(false);
                            setSelectedItem(null);
                            setEditSchoolYearData({
                              title: "",
                              startDate: "",
                              endDate: "",
                              isActive: false,
                              type: "",
                            });
                          } else {
                            setShowAddYearModal(false);
                            setNewSchoolYear({
                              title: "",
                              startDate: "",
                              endDate: "",
                              isActive: false,
                            });
                          }
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap`}
                      >
                        Annuler
                      </button>
                      <button
                        disabled={
                          mutationCreateShoolYear.isPending ||
                          mutationUpdateShoolYear.isPending
                        }
                        onClick={() => {
                          if (showEditModal) {
                            handleEditYear();
                          } else {
                            handleSaveNewYear();
                          }
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap ${
                          mutationCreateShoolYear.isPending ||
                          mutationUpdateShoolYear.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateShoolYear.isPending ||
                        mutationUpdateShoolYear.isPending
                          ? " Enregistrer..."
                          : " Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={`mb-6 rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Année
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Date de début
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Date de fin
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Statut
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {schoolYearArr.map((year: any) => {
                    return (
                      <tr key={year.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            {year.designation}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {moment(year.date_debut).format("DD/MM/YYYY")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {moment(year.date_fin).format("DD/MM/YYYY")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              year.status
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {year.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex justify-center space-x-3">
                            <button
                              onClick={() => {
                                setShowEditModal(true);
                                setEditSchoolYearData({
                                  id: year.id,
                                  title: year.designation,
                                  startDate: year.date_debut,
                                  endDate: year.date_fin,
                                  isActive: year.status,
                                  type: "",
                                });
                              }}
                              className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              onClick={() => {
                                dispatch({
                                  type: "DELETE",
                                  payload: {
                                    actionText:
                                      "Êtes-vous sûr de vouloir supprimer cette année scolaire? Cette action est irréversible",
                                    currentId: year.id,
                                    actionSection: "schoolYear",
                                  },
                                });
                              }}
                              className="text-red-500 hover:text-red-700 cursor-pointer !rounded-button whitespace-nowrap"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                            <button
                              disabled={year.status}
                              onClick={() => {
                                dispatch({
                                  type: "ACTIVATE",
                                  payload: {
                                    actionText:
                                      "Êtes-vous sûr de vouloir activer cette année scolaire?",
                                    currentId: year.id,
                                    actionSection: "schoolYear",
                                  },
                                });
                              }}
                              className={`${
                                year.status
                                  ? "text-green-500 hover:text-green-700"
                                  : "text-gray-500 hover:text-green-700"
                              } cursor-pointer !rounded-button whitespace-nowrap`}
                            >
                              <i className="fa-solid fa-circle-check"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Classes</h4>
              <button
                id="addClassBtn"
                onClick={() => setShowAddClassModal(true)}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter une classe
              </button>
              {(showAddClassModal || showEditClassModal) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div
                    className={`${
                      isDarkMode ? "bg-[#1c2128]" : "bg-white"
                    } rounded-lg shadow-xl w-full max-w-md mx-4`}
                  >
                    <div
                      className={`px-6 py-4 border-b ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <h3 className="text-xl font-medium">
                        {showEditClassModal
                          ? "Modifier la classe"
                          : "Ajouter une nouvelle classe"}
                      </h3>
                    </div>
                    <div className="px-6 py-4">
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Nom de la classe
                        </label>
                        <input
                          type="text"
                          value={
                            showEditClassModal
                              ? editClassData.name
                              : newClass.name
                          }
                          onChange={(e) =>
                            showEditClassModal
                              ? setEditClassData({
                                  ...editClassData,
                                  name: e.target.value,
                                })
                              : setNewClass({
                                  ...newClass,
                                  name: e.target.value,
                                })
                          }
                          placeholder="6ème A"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Code de la classe
                        </label>
                        <input
                          type="text"
                          value={
                            showEditClassModal
                              ? editClassData.code
                              : newClass.code
                          }
                          onChange={(e) =>
                            showEditClassModal
                              ? setEditClassData({
                                  ...editClassData,
                                  code: e.target.value,
                                })
                              : setNewClass({
                                  ...newClass,
                                  code: e.target.value,
                                })
                          }
                          placeholder="6 Eb A"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Niveau
                        </label>
                        <select
                          value={
                            showEditClassModal
                              ? editClassData.level
                              : newClass.level
                          }
                          onChange={(e) =>
                            showEditClassModal
                              ? setEditClassData({
                                  ...editClassData,
                                  level: e.target.value,
                                })
                              : setNewClass({
                                  ...newClass,
                                  level: e.target.value,
                                })
                          }
                          className={`w-full px-3 py-1 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                          <option value="Collège">Ecole Secondaire</option>
                          {/* <option value="Lycée">Lycée</option>
                            <option value="Primaire">Primaire</option> */}
                        </select>
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Année scolaire
                        </label>
                        <select
                          className={`w-full px-3 py-1 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                          <option value="">{activeYear}</option>
                        </select>
                      </div>
                    </div>
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        disabled={
                          mutationCreateClass.isPending ||
                          mutationUpdateClass.isPending
                        }
                        onClick={() => {
                          showEditClassModal
                            ? setShowEditClassModal(false)
                            : setShowAddClassModal(false);
                          setNewClass({
                            name: "",
                            code: "",
                            level: "",
                            schoolYear: "",
                          });
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap ${
                          mutationCreateClass.isPending ||
                          mutationUpdateClass.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Annuler
                      </button>
                      <button
                        disabled={
                          mutationCreateClass.isPending ||
                          mutationUpdateClass.isPending
                        }
                        onClick={() => {
                          // Handle save logic here
                          showEditClassModal
                            ? handleEditClass()
                            : handleSaveNewClass();
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap ${
                          mutationCreateClass.isPending ||
                          mutationUpdateClass.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateClass.isPending ||
                        mutationUpdateClass.isPending
                          ? "Enregrement en cours..."
                          : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mb-4">
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex-grow relative">
                  <div
                    className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500`}
                  >
                    <i className="fas fa-search"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher une classe..."
                    className={`w-full pl-10 pr-4 py-2 rounded-md text-sm ${
                      isDarkMode
                        ? "bg-[#161B22] border-gray-700"
                        : "bg-white border-gray-300"
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={toggleYearClassDropdown}
                    className={`flex items-center px-4 py-2 rounded-md ${
                      isDarkMode
                        ? "bg-[#161B22] hover:bg-gray-700"
                        : "bg-white hover:bg-gray-100"
                    } border ${
                      isDarkMode ? "border-gray-700" : "border-gray-300"
                    } !rounded-button whitespace-nowrap`}
                  >
                    <span>{filtrerClasseByYear}</span>
                    <i className="fas fa-chevron-down ml-2"></i>
                  </button>
                  {isYearClassDropdownOpen && (
                    <div
                      className={`absolute top-full right-0 mt-1 w-48 rounded-md shadow-lg ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } z-10 border ${
                        isDarkMode ? "border-gray-700" : "border-gray-300"
                      }`}
                      ref={yearClassDropdownRef}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setfiltrerClasseByYear("Toutes les années");
                            setIsYearClassDropdownOpen(false);
                          }}
                          className={`block px-4 py-2 text-sm w-full text-left ${
                            isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-100"
                          } !rounded-button whitespace-nowrap`}
                        >
                          Toutes les années
                        </button>

                        {years.map((year, index) => {
                          return (
                            <button
                              key={index}
                              onClick={() => {
                                setfiltrerClasseByYear(year);
                                setIsYearClassDropdownOpen(false);
                              }}
                              className={`block px-4 py-2 text-sm w-full text-left ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } !rounded-button whitespace-nowrap`}
                            >
                              {year}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={`rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Nom
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Code - Classe
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Niveau
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {filteredClasses.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          {capitalizeFirstLetter(item.designation)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        {capitalizeFirstLetter(item.code)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        Ecole Sécondaire
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => {
                              setEditClassData({
                                id: item.id,
                                name: item.designation,
                                code: item.code,
                                level: "",
                                schoolYear: "",
                              });
                              setShowEditClassModal(true);
                            }}
                            className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => {
                              dispatch({
                                type: "DELETE",
                                payload: {
                                  actionText:
                                    "Êtes-vous sûr de vouloir supprimer cette classe? Cette action est irréversible",
                                  currentId: item.id,
                                  actionSection: "class",
                                },
                              });
                            }}
                            className="text-red-500 hover:text-red-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                          <button
                            onClick={() => {
                              item.status
                                ? dispatch({
                                    type: "DESACTIVATE",
                                    payload: {
                                      actionText:
                                        "Êtes-vous sûr de vouloir desactiver cette classe?",
                                      currentId: item.id,
                                      actionSection: "class",
                                    },
                                  })
                                : dispatch({
                                    type: "ACTIVATE",
                                    payload: {
                                      actionText:
                                        "Êtes-vous sûr de vouloir activer cette classe?",
                                      currentId: item.id,
                                      actionSection: "class",
                                    },
                                  });
                            }}
                            className={`${
                              item.status
                                ? "text-green-500 hover:text-green-700"
                                : "text-gray-500 hover:text-green-700"
                            } cursor-pointer !rounded-button whitespace-nowrap`}
                          >
                            <i className="fa-solid fa-circle-check"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Grades et fonctions */}
      <div
        className={`rounded-lg overflow-hidden border ${
          isDarkMode ? "border-gray-700" : "border-gray-300"
        }`}
      >
        <div
          className={`p-4 flex justify-between items-center cursor-pointer ${
            isDarkMode ? "bg-[#161B22]" : "bg-gray-50"
          }`}
          onClick={() => toggleScolariteAccordion("grades")}
        >
          <div className="flex items-center">
            <i className="fas fa-user-tie text-purple-500 mr-3"></i>
            <h3 className="text-lg font-medium">Grades et fonctions</h3>
          </div>
          <i
            className={`fas ${
              activeScolariteAccordion === "grades"
                ? "fa-chevron-up"
                : "fa-chevron-down"
            } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          ></i>
        </div>
        {activeScolariteAccordion === "grades" && (
          <div className={`p-5 ${isDarkMode ? "bg-[#0D1117]" : "bg-white"}`}>
            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Domaines d'études</h4>
              <button
                onClick={() => setShowAddDomaineModal(true)}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter un domaine
              </button>

              {(showAddDomaineModal || showEditDomaineModal) && (
                <div
                  id="addDomainModal"
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                >
                  <div
                    className={`${
                      isDarkMode ? "bg-[#1c2128]" : "bg-white"
                    } rounded-lg shadow-xl w-full max-w-md mx-4`}
                  >
                    {/* Header */}
                    <div
                      className={`px-6 py-4 border-b ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-between items-center`}
                    >
                      <h3 className="text-xl font-medium">
                        {showEditDomaineModal
                          ? "Modifier domaine"
                          : " Ajouter un nouveau domaine"}
                      </h3>
                      <button
                        onClick={() => {
                          showEditDomaineModal
                            ? setShowEditDomaineModal(false)
                            : setShowAddDomaineModal(false);

                          setCharCount(0);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>

                    {/* Form */}
                    <div className="px-6 py-4">
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Nom du domaine *
                        </label>
                        <input
                          type="text"
                          value={
                            showEditDomaineModal
                              ? editDomainData.designation
                              : newDomain.designation
                          }
                          onChange={(e) => {
                            showEditDomaineModal
                              ? setEditDomainData((prev) => ({
                                  ...prev,
                                  designation: e.target.value,
                                }))
                              : setNewDomain((prev) => ({
                                  ...prev,
                                  designation: e.target.value,
                                }));
                          }}
                          placeholder="Ex: Sciences appliquées"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        />
                      </div>

                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Description *
                        </label>
                        <textarea
                          value={
                            showEditDomaineModal
                              ? editDomainData.description
                              : newDomain.description
                          }
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>
                          ) => {
                            const value = e.target.value.slice(0, 300);
                            showEditDomaineModal
                              ? setEditDomainData((prev) => ({
                                  ...prev,
                                  description: value,
                                }))
                              : setNewDomain((prev) => ({
                                  ...prev,
                                  description: value,
                                }));

                            setCharCount(value.length);
                          }}
                          rows={4}
                          placeholder="Décrivez les matières et objectifs pédagogiques de ce domaine..."
                          maxLength={300}
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                          required
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {charCount}/300 caractères
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        disabled={
                          mutationCreateDomain.isPending ||
                          mutationUpdateDomain.isPending
                        }
                        onClick={() => {
                          showEditDomaineModal
                            ? setShowEditDomaineModal(false)
                            : setShowAddDomaineModal(false);

                          setCharCount(0);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap ${
                          mutationCreateDomain.isPending ||
                          mutationUpdateDomain.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Annuler
                      </button>

                      <button
                        disabled={
                          mutationCreateDomain.isPending ||
                          mutationUpdateDomain.isPending
                        }
                        onClick={() => {
                          // Handle save logic here
                          showEditDomaineModal
                            ? handleEditDomain()
                            : handleSaveNewDomain();

                          setCharCount(0);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap  ${
                          mutationCreateDomain.isPending ||
                          mutationUpdateDomain.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateDomain.isPending ||
                        mutationUpdateDomain.isPending
                          ? "Enregistrer en cours..."
                          : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={`mb-6 rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Nom
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {domainDataArr.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          {capitalizeFirstLetter(item.designation)}
                        </span>
                      </td>
                      <td className="px-6 py-4">{item.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => {
                              setEditDomainData({
                                id: item.id,
                                designation: item.designation,
                                description: item.code,
                              });
                              setShowEditDomaineModal(true);
                            }}
                            className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            disabled={true}
                            // onClick={() => {
                            //   dispatch({
                            //     type: "DELETE",
                            //     payload: {
                            //       actionText:
                            //         "Êtes-vous sûr de vouloir supprimer ce domaine? Cette action est irréversible",
                            //       currentId: item.id,
                            //       actionSection: "domain",
                            //     },
                            //   });
                            // }}
                            className="text-gray-500 hover:text-gray-700 cursor-not-allowed !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Fonctions du personnel</h4>
              <button
                onClick={() => setShowAddFunctionModal(true)}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter une fonction
              </button>
            </div>

            {(showAddFunctionModal || showEditFunctionModal) && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div
                  className={`${
                    isDarkMode ? "bg-[#1c2128]" : "bg-white"
                  } rounded-lg shadow-xl w-full max-w-md mx-4`}
                >
                  {/* Header */}
                  <div
                    className={`px-6 py-4 border-b ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    } flex justify-between items-center`}
                  >
                    <h3 className="text-xl font-medium">
                      {showEditFunctionModal
                        ? "Modifier Fonction"
                        : " Ajouter un nouveau Fonction"}
                    </h3>
                    <button
                      onClick={() => {
                        showEditFunctionModal
                          ? setShowEditFunctionModal(false)
                          : setShowAddFunctionModal(false);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  {/* Body */}
                  <div className="px-6 py-4">
                    {/* Function Title */}
                    <div className="mb-4">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Nom de la fonction *
                      </label>
                      <input
                        type="text"
                        value={
                          showEditFunctionModal
                            ? editFunctionData.designation
                            : newFunction.designation
                        }
                        onChange={(e) => {
                          showEditFunctionModal
                            ? setEditFunctionData((prev) => ({
                                ...prev,
                                designation: e.target.value,
                              }))
                            : setNewFunction((prev) => ({
                                ...prev,
                                designation: e.target.value,
                              }));
                        }}
                        placeholder="Ex: Professeur principal"
                        className={`w-full px-3 py-2 rounded-md border ${
                          isDarkMode
                            ? "bg-[#161B22] border-gray-700 text-white"
                            : "bg-white border-gray-300"
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        required
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Description des responsabilités *
                      </label>
                      <textarea
                        id="functionDescription"
                        name="functionDescription"
                        rows={4}
                        value={
                          showEditFunctionModal
                            ? editFunctionData.description
                            : newFunction.description
                        }
                        onChange={(
                          e: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          const value = e.target.value.slice(0, 300);
                          showEditFunctionModal
                            ? setEditFunctionData((prev) => ({
                                ...prev,
                                description: value,
                              }))
                            : setNewFunction((prev) => ({
                                ...prev,
                                description: value,
                              }));

                          setCharCountFunction(value.length);
                        }}
                        placeholder="Décrivez les principales responsabilités..."
                        maxLength={300}
                        className={`w-full px-3 py-2 rounded-md border ${
                          isDarkMode
                            ? "bg-[#161B22] border-gray-700 text-white"
                            : "bg-white border-gray-300"
                        } focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                        required
                      ></textarea>
                      <div className="text-xs text-gray-500 mt-1">
                        <span className={charCount >= 50 ? "text-red-500" : ""}>
                          {charCountFunction}
                        </span>
                        /300 caractères
                      </div>
                    </div>

                    {/* Function Level */}
                    {/* <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Niveau hiérarchique *
                        </label>
                        <select
                          id="functionLevel"
                          name="functionLevel"
                          // value={functionLevel}
                          // onChange={(e) => setFunctionLevel(e.target.value)}
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        >
                          <option value="">Sélectionner un niveau</option>
                          <option value="1">Niveau 1 - Direction</option>
                          <option value="2">Niveau 2 - Encadrement</option>
                          <option value="3">Niveau 3 - Enseignement</option>
                          <option value="4">Niveau 4 - Support</option>
                        </select>
                      </div> */}

                    {/* Permissions
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Permissions associées
                        </label>
                        <div className="space-y-2">
                          {[
                            { id: "gestion", label: "Gestion des élèves" },
                            { id: "notes", label: "Saisie des notes" },
                            { id: "presence", label: "Gestion des présences" },
                            { id: "communication", label: "Communications" },
                            { id: "rapports", label: "Accès aux rapports" },
                          ].map((perm) => (
                            <label
                              key={perm.id}
                              className="inline-flex items-center"
                            >
                              <input
                                type="checkbox"
                                // checked={permissions.includes(perm.id)}
                                // onChange={() => handlePermissionChange(perm.id)}
                                className="form-checkbox"
                              />
                              <span className="ml-2">{perm.label}</span>
                            </label>
                          ))}
                        </div>
                      </div> */}

                    {/* Footer */}
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        disabled={
                          mutationCreateFunction.isPending ||
                          mutationUpdateFunction.isPending
                        }
                        onClick={() => {
                          showEditFunctionModal
                            ? setShowEditFunctionModal(false)
                            : setShowAddFunctionModal(false);

                          setCharCountFunction(0);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap ${
                          mutationCreateFunction.isPending ||
                          mutationUpdateFunction.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Annuler
                      </button>

                      <button
                        disabled={
                          mutationCreateFunction.isPending ||
                          mutationUpdateFunction.isPending
                        }
                        onClick={() => {
                          // Handle save logic here
                          showEditFunctionModal
                            ? handleEditFunction()
                            : handleSaveNewFunction();

                          setCharCountFunction(0);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap  ${
                          mutationCreateFunction.isPending ||
                          mutationUpdateFunction.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateFunction.isPending ||
                        mutationUpdateFunction.isPending
                          ? "Enregistrer en cours..."
                          : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div
              className={`mb-6 rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Titre
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {functionDataArr.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          {capitalizeFirstLetter(item.designation)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {capitalizeFirstLetter(item.code)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => {
                              setEditFunctionData({
                                id: item.id,
                                designation: item.designation,
                                description: item.code,
                              });
                              setShowEditFunctionModal(true);
                            }}
                            className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            disabled={true}
                            // onClick={() => {
                            //   dispatch({
                            //     type: "DELETE",
                            //     payload: {
                            //       actionText:
                            //         "Êtes-vous sûr de vouloir supprimer ce domaine? Cette action est irréversible",
                            //       currentId: item.id,
                            //       actionSection: "domain",
                            //     },
                            //   });
                            // }}
                            className="text-gray-500 hover:text-gray-700 cursor-not-allowed !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Grades</h4>
              <button
                onClick={() => setShowAddGradeModal(true)}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter un grade
              </button>

              {(showAddGradeModal || showEditGradeModal) && (
                <div
                  id="addDomainModal"
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                >
                  <div
                    className={`${
                      isDarkMode ? "bg-[#1c2128]" : "bg-white"
                    } rounded-lg shadow-xl w-full max-w-md mx-4`}
                  >
                    {/* Header */}
                    <div
                      className={`px-6 py-4 border-b ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-between items-center`}
                    >
                      <h3 className="text-xl font-medium">
                        {showEditGradeModal
                          ? "Modifier grade"
                          : " Ajouter un nouveau grade"}
                      </h3>
                      <button
                        onClick={() => {
                          showEditGradeModal
                            ? setShowEditGradeModal(false)
                            : setShowAddGradeModal(false);

                          setCharCountGrade(0);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>

                    {/* Form */}
                    <div className="px-6 py-4">
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Nom du grade *
                        </label>
                        <input
                          type="text"
                          value={
                            showEditGradeModal
                              ? editGradeData.designation
                              : newGrade.designation
                          }
                          onChange={(e) => {
                            showEditGradeModal
                              ? setEditGradeData((prev) => ({
                                  ...prev,
                                  designation: e.target.value,
                                }))
                              : setNewGrade((prev) => ({
                                  ...prev,
                                  designation: e.target.value,
                                }));
                          }}
                          placeholder="Ex: Sciences appliquées"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        />
                      </div>

                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Description *
                        </label>
                        <textarea
                          value={
                            showEditGradeModal
                              ? editGradeData.description
                              : newGrade.description
                          }
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>
                          ) => {
                            const value = e.target.value.slice(0, 300);
                            showEditGradeModal
                              ? setEditGradeData((prev) => ({
                                  ...prev,
                                  description: value,
                                }))
                              : setNewGrade((prev) => ({
                                  ...prev,
                                  description: value,
                                }));

                            setCharCountGrade(value.length);
                          }}
                          rows={4}
                          placeholder="Décrivez les attributions de ce grade..."
                          maxLength={300}
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                          required
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {charCountGrade}/300 caractères
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        disabled={
                          mutationCreateGrade.isPending ||
                          mutationUpdateGrade.isPending
                        }
                        onClick={() => {
                          showEditGradeModal
                            ? setShowEditGradeModal(false)
                            : setShowAddGradeModal(false);

                          setCharCountGrade(0);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap ${
                          mutationCreateGrade.isPending ||
                          mutationUpdateGrade.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Annuler
                      </button>

                      <button
                        disabled={
                          mutationCreateGrade.isPending ||
                          mutationUpdateGrade.isPending
                        }
                        onClick={() => {
                          // Handle save logic here
                          showEditGradeModal
                            ? handleEditGrade()
                            : handleSaveNewGrade();

                          setCharCountGrade(0);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap  ${
                          mutationCreateGrade.isPending ||
                          mutationUpdateGrade.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateGrade.isPending ||
                        mutationUpdateGrade.isPending
                          ? "Enregistrer en cours..."
                          : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={`rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Intitulé
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {gradeDataArr.map((item) => (
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          {capitalizeFirstLetter(item.designation)}
                        </span>
                      </td>
                      <td className="px-6 py-4">{item.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => {
                              setEditGradeData({
                                id: item.id,
                                designation: item.designation,
                                description: item.code,
                              });
                              setShowEditGradeModal(true);
                            }}
                            className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            disabled={true}
                            // onClick={() => {
                            //   dispatch({
                            //     type: "DELETE",
                            //     payload: {
                            //       actionText:
                            //         "Êtes-vous sûr de vouloir supprimer ce domaine? Cette action est irréversible",
                            //       currentId: item.id,
                            //       actionSection: "domain",
                            //     },
                            //   });
                            // }}
                            className="text-gray-500 hover:text-gray-700 cursor-not-allowed !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Frais scolaires */}
      <div
        className={`rounded-lg overflow-hidden border ${
          isDarkMode ? "border-gray-700" : "border-gray-300"
        }`}
      >
        <div
          className={`p-4 flex justify-between items-center cursor-pointer ${
            isDarkMode ? "bg-[#161B22]" : "bg-gray-50"
          }`}
          onClick={() => toggleScolariteAccordion("frais")}
        >
          <div className="flex items-center">
            <i className="fas fa-euro-sign text-green-500 mr-3"></i>
            <h3 className="text-lg font-medium">Frais scolaires</h3>
          </div>
          <i
            className={`fas ${
              activeScolariteAccordion === "frais"
                ? "fa-chevron-up"
                : "fa-chevron-down"
            } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          ></i>
        </div>
        {activeScolariteAccordion === "frais" && (
          <div className={`p-5 ${isDarkMode ? "bg-[#0D1117]" : "bg-white"}`}>
            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Liste des frais scolaires</h4>
              <button
                onClick={() => setShowAddMontantModal(true)}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter des frais
              </button>

              {(showAddMontantModal || showEditMontantModal) && (
                <div
                  id="addTerminalModal"
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                >
                  <div
                    className={`${
                      isDarkMode ? "bg-[#1c2128]" : "bg-white"
                    } rounded-lg shadow-xl w-full max-w-lg mx-4`}
                  >
                    <div
                      className={`px-6 py-4 border-b ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-between items-center`}
                    >
                      <h3 className="text-xl font-medium">
                        {showEditMontantModal
                          ? "Modifier Frais scolaire"
                          : " Ajouter un frais scolaire"}
                      </h3>
                      <button
                        id="closeTerminalModalBtn"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => {
                          showEditMontantModal
                            ? setShowEditMontantModal(false)
                            : setShowAddMontantModal(false);
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                    <div className="px-6 py-4">
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Classe *
                        </label>
                        <select
                          value={
                            showEditMontantModal
                              ? editMontantData.classe_id
                              : newMontant.classe_id
                          }
                          onChange={(e) => {
                            showEditMontantModal
                              ? setEditMontantData((prev) => ({
                                  ...prev,
                                  classe_id: e.target.value,
                                }))
                              : setNewMontant((prev) => ({
                                  ...prev,
                                  classe_id: e.target.value,
                                }));
                          }}
                          className={`w-full px-3 py-1 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        >
                          <option value="">Sélectionner une classe</option>
                          {classeDataArr.map((item) => (
                            <option value={item.id}>
                              {capitalizeFirstLetter(item.designation)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Année scolaire *
                        </label>
                        <select
                          onChange={(e) => {
                            showEditMontantModal
                              ? setEditMontantData((prev) => ({
                                  ...prev,
                                  annee_scolaire_id: e.target.value,
                                }))
                              : setNewMontant((prev) => ({
                                  ...prev,
                                  annee_scolaire_id: e.target.value,
                                }));
                          }}
                          className={`w-full px-3 py-1 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        >
                          <option value={activeYearId}>{activeYear}</option>
                        </select>
                      </div>
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Montant
                        </label>
                        <input
                          type="number"
                          value={
                            showEditMontantModal
                              ? editMontantData.montant
                              : newMontant.montant
                          }
                          onChange={(e) => {
                            showEditMontantModal
                              ? setEditMontantData((prev) => ({
                                  ...prev,
                                  montant: e.target.value,
                                }))
                              : setNewMontant((prev) => ({
                                  ...prev,
                                  montant: e.target.value,
                                }));
                          }}
                          placeholder="50 $"
                          min="1"
                          max="10000"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                          required
                        />
                      </div>
                    </div>
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        disabled={
                          mutationCreateMontant.isPending ||
                          mutationUpdateMontant.isPending
                        }
                        onClick={() => {
                          showEditMontantModal
                            ? setShowEditMontantModal(false)
                            : setShowAddMontantModal(false);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap ${
                          mutationCreateMontant.isPending ||
                          mutationUpdateMontant.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Annuler
                      </button>

                      <button
                        disabled={
                          mutationCreateMontant.isPending ||
                          mutationUpdateMontant.isPending
                        }
                        onClick={() => {
                          showEditMontantModal
                            ? handleEditMontant()
                            : handleSaveNewMontant();
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap  ${
                          mutationCreateMontant.isPending ||
                          mutationUpdateMontant.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateMontant.isPending ||
                        mutationUpdateMontant.isPending
                          ? "Enregistrement en cours..."
                          : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mb-4">
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex-grow relative">
                  <div
                    className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500`}
                  >
                    <i className="fas fa-search"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher des frais..."
                    className={`w-full pl-10 pr-4 py-2 rounded-md text-sm ${
                      isDarkMode
                        ? "bg-[#161B22] border-gray-700"
                        : "bg-white border-gray-300"
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={toggleMontantSyDropdown}
                    className={`flex items-center px-4 py-2 rounded-md ${
                      isDarkMode
                        ? "bg-[#161B22] hover:bg-gray-700"
                        : "bg-white hover:bg-gray-100"
                    } border ${
                      isDarkMode ? "border-gray-700" : "border-gray-300"
                    } !rounded-button whitespace-nowrap`}
                  >
                    <span>Année: {filterMontantSy}</span>
                    <i className="fas fa-chevron-down ml-2"></i>
                  </button>
                  {isMontantSyDropdownOpen && (
                    <div
                      className={`absolute bottom-[0px] right-0 mt-1 w-48 rounded-md shadow-lg ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } z-10 border ${
                        isDarkMode ? "border-gray-700" : "border-gray-300"
                      }`}
                      ref={montantSyDropdownRef}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setFilterMontantBySy("Toutes");
                            setIsMontantSyDropdownOpen(false);
                          }}
                          className={`block px-4 py-2 text-sm w-full text-left ${
                            isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-100"
                          } !rounded-button whitespace-nowrap`}
                        >
                          Toutes
                        </button>

                        {schoolYearArr.map((year, index) => {
                          return (
                            <button
                              key={index}
                              onClick={() => {
                                setFilterMontantBySy(
                                  year.designation.toLowerCase()
                                );
                                setIsMontantSyDropdownOpen(false);
                              }}
                              className={`block px-4 py-2 text-sm w-full text-left ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } !rounded-button whitespace-nowrap`}
                            >
                              {year.designation.toLowerCase()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={toggleMontantClassDropdown}
                    className={`flex items-center px-4 py-2 rounded-md ${
                      isDarkMode
                        ? "bg-[#161B22] hover:bg-gray-700"
                        : "bg-white hover:bg-gray-100"
                    } border ${
                      isDarkMode ? "border-gray-700" : "border-gray-300"
                    } !rounded-button whitespace-nowrap`}
                  >
                    <span>{filterMontantByClasse}</span>
                    <i className="fas fa-chevron-down ml-2"></i>
                  </button>
                  {isMontantClassDropdownOpen && (
                    <div
                      className={`fixed z-50 top-16 right-0 mt-1 w-48 rounded-md shadow-lg ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } z-10 border ${
                        isDarkMode ? "border-gray-700" : "border-gray-300"
                      }`}
                      ref={montantClassDropdownRef}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setFilterMontantByClasse("Toutes les classes");
                            setIsMontantClassDropdownOpen(false);
                          }}
                          className={`block px-4 py-2 text-sm w-full text-left ${
                            isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-100"
                          } !rounded-button whitespace-nowrap`}
                        >
                          Toutes les classes
                        </button>

                        {classeDataArr.map((classe, index) => {
                          return (
                            <button
                              key={index}
                              onClick={() => {
                                setFilterMontantByClasse(classe.designation);
                                setIsMontantClassDropdownOpen(false);
                              }}
                              className={`block px-4 py-2 text-sm w-full text-left ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } !rounded-button whitespace-nowrap`}
                            >
                              {classe.designation.toLowerCase()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  className={`px-4 py-2 rounded-md ${
                    isDarkMode
                      ? "bg-[#161B22] hover:bg-gray-700"
                      : "bg-white hover:bg-gray-100"
                  } border ${
                    isDarkMode ? "border-gray-700" : "border-gray-300"
                  } !rounded-button whitespace-nowrap`}
                  title="Reinitialiser"
                  onClick={handleResetFilter}
                >
                  <i className="fas fa-filter"></i>
                </button>
              </div>
            </div>
            <div
              className={`rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Classe
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Montant
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Période
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Obligatoire
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {filteredMontants.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          {capitalizeFirstLetter(item.classeDesignation)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.montant} $
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.anneeScolaireDesignation}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Oui
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => {
                              setEditMontantData({
                                id: item.id,
                                classDesignation: item.classeDesignation,
                                montant: item.montant,
                                classe_id: item.classe_id,
                                annee_scolaire_id: item.annee_scolaire_id,
                              });
                              setShowEditMontantModal(true);
                            }}
                            className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            // disabled={true}
                            onClick={() => {
                              dispatch({
                                type: "DELETE",
                                payload: {
                                  actionText:
                                    "Êtes-vous sûr de vouloir supprimer ce montant? Cette action est irréversible",
                                  currentId: item.id,
                                  actionSection: "montant",
                                },
                              });
                            }}
                            className="text-red-500 hover:text-red-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Terminaux POS */}
      <div
        className={`rounded-lg overflow-hidden border ${
          isDarkMode ? "border-gray-700" : "border-gray-300"
        }`}
      >
        <div
          className={`p-4 flex justify-between items-center cursor-pointer ${
            isDarkMode ? "bg-[#161B22]" : "bg-gray-50"
          }`}
          onClick={() => toggleScolariteAccordion("terminaux")}
        >
          <div className="flex items-center">
            <i className="fas fa-credit-card text-yellow-500 mr-3"></i>
            <h3 className="text-lg font-medium">Terminaux POS</h3>
          </div>
          <i
            className={`fas ${
              activeScolariteAccordion === "terminaux"
                ? "fa-chevron-up"
                : "fa-chevron-down"
            } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          ></i>
        </div>
        {activeScolariteAccordion === "terminaux" && (
          <div className={`p-5 ${isDarkMode ? "bg-[#0D1117]" : "bg-white"}`}>
            <div className="mb-4 flex justify-between items-center">
              <h4 className="text-lg font-medium">Liste des terminaux</h4>
              <button
                onClick={() => setShowAddTerminalModal(true)}
                className={`px-3 py-1 rounded-md ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter un terminal
              </button>

              {(showAddTerminalModal || showEditTerminalModal) && (
                <div
                  id="addDomainModal"
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                >
                  <div
                    className={`${
                      isDarkMode ? "bg-[#1c2128]" : "bg-white"
                    } rounded-lg shadow-xl w-full max-w-md mx-4`}
                  >
                    {/* Header */}
                    <div
                      className={`px-6 py-4 border-b ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-between items-center`}
                    >
                      <h3 className="text-xl font-medium">
                        {showEditTerminalModal
                          ? "Modifier terminal"
                          : " Ajouter un nouveau terminal"}
                      </h3>
                      <button
                        onClick={() => {
                          showEditTerminalModal
                            ? setShowEditTerminalModal(false)
                            : setShowAddTerminalModal(false);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>

                    {/* Form */}
                    <div className="px-6 py-4">
                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Nom du terminal *
                        </label>
                        <input
                          type="text"
                          value={
                            showEditTerminalModal
                              ? editTerminalData.designation
                              : newTerminal.designation
                          }
                          onChange={(e) => {
                            showEditTerminalModal
                              ? setEditTerminalData((prev) => ({
                                  ...prev,
                                  designation: e.target.value,
                                }))
                              : setNewTerminal((prev) => ({
                                  ...prev,
                                  designation: e.target.value,
                                }));
                          }}
                          placeholder="Ex: Post paiement"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        />
                      </div>

                      <div className="mb-4">
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Identifiant du terminal *
                        </label>
                        <input
                          type="text"
                          value={
                            showEditTerminalModal
                              ? editTerminalData.uid
                              : newTerminal.uid
                          }
                          onChange={(e) => {
                            showEditTerminalModal
                              ? setEditTerminalData((prev) => ({
                                  ...prev,
                                  uid: e.target.value,
                                }))
                              : setNewTerminal((prev) => ({
                                  ...prev,
                                  uid: e.target.value,
                                }));
                          }}
                          placeholder="Ex:06ba423607t28a90"
                          className={`w-full px-3 py-2 rounded-md border ${
                            isDarkMode
                              ? "bg-[#161B22] border-gray-700 text-white"
                              : "bg-white border-gray-300"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          required
                        />
                      </div>
                    </div>

                    {/* Footer */}
                    <div
                      className={`px-6 py-4 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      } flex justify-end space-x-3`}
                    >
                      <button
                        disabled={
                          mutationCreateTerminal.isPending ||
                          mutationUpdateTerminal.isPending
                        }
                        onClick={() => {
                          showEditTerminalModal
                            ? setShowEditTerminalModal(false)
                            : setShowAddTerminalModal(false);
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600"
                            : "bg-gray-200 hover:bg-gray-300"
                        } !rounded-button whitespace-nowrap ${
                          mutationCreateTerminal.isPending ||
                          mutationUpdateTerminal.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Annuler
                      </button>

                      <button
                        disabled={
                          mutationCreateTerminal.isPending ||
                          mutationUpdateTerminal.isPending
                        }
                        onClick={() => {
                          // Handle save logic here
                          showEditTerminalModal
                            ? handleEditTerminal()
                            : handleSaveNewTerminal();
                        }}
                        className={`px-4 py-2 rounded-md ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap  ${
                          mutationCreateTerminal.isPending ||
                          mutationUpdateTerminal.isPending
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {mutationCreateTerminal.isPending ||
                        mutationUpdateTerminal.isPending
                          ? "Enregistrer en cours..."
                          : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={`rounded-lg overflow-hidden border ${
                isDarkMode ? "border-gray-700" : "border-gray-300"
              }`}
            >
              <table className="min-w-full divide-y divide-gray-700">
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Nom terminal
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Identifiant
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Statut
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {terminalDataArr.map((item, index) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          POS-00{index + 1}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {capitalizeFirstLetter(item.designation)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.uid}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            item.status
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          } `}
                        >
                          {item.status ? "Actif" : "Inactif"}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => {
                              setEditTerminalData({
                                id: item.id,
                                uid: item.uid,
                                designation: item.designation,
                              });
                              setShowEditTerminalModal(true);
                            }}
                            className="text-green-500 hover:text-green-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            // disabled={true}
                            onClick={() => {
                              dispatch({
                                type: "DELETE",
                                payload: {
                                  actionText:
                                    "Êtes-vous sûr de vouloir supprimer ce terminal? Cette action est irréversible",
                                  currentId: item.id,
                                  actionSection: "terminal",
                                },
                              });
                            }}
                            className="text-red-500 hover:text-red-700 cursor-pointer !rounded-button whitespace-nowrap"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* delete dialog */}
      {state_actions.actionType === "delete" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`${
              isDarkMode ? "bg-[#1c2128]" : "bg-white"
            } rounded-lg shadow-xl w-full max-w-md mx-4 p-6`}
          >
            <div className="text-xl font-medium mb-4">
              Confirmer la suppression
            </div>
            <p
              className={`${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              } mb-6`}
            >
              {state_actions.actionText}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                disabled={
                  mutationDeleteShoolYear.isPending ||
                  mutationDeleteClass.isPending ||
                  mutationDeleteDomain.isPending ||
                  mutationDeleteMontant.isPending
                }
                onClick={() =>
                  dispatch({
                    type: "CANCEL",
                    payload: {
                      actionText: "",
                      currentId: null,
                      actionSection: "",
                    },
                  })
                }
                className={`px-4 py-2 rounded-md ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                } !rounded-button whitespace-nowrap ${
                  mutationDeleteShoolYear.isPending ||
                  mutationDeleteClass.isPending ||
                  mutationDeleteDomain.isPending ||
                  mutationDeleteMontant.isPending
                    ? "cursor-not-allowed"
                    : ""
                }`}
              >
                Annuler
              </button>
              <button
                disabled={
                  mutationDeleteShoolYear.isPending ||
                  mutationDeleteClass.isPending ||
                  mutationDeleteDomain.isPending ||
                  mutationDeleteTerminal.isPending ||
                  mutationDeleteMontant.isPending
                }
                onClick={handleDelete}
                className={`px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 text-white !rounded-button whitespace-nowrap ${
                  mutationDeleteShoolYear.isPending ||
                  mutationDeleteClass.isPending ||
                  mutationDeleteDomain.isPending ||
                  mutationDeleteTerminal.isPending ||
                  mutationDeleteMontant.isPending
                    ? "cursor-not-allowed"
                    : ""
                }`}
              >
                {mutationDeleteShoolYear.isPending ||
                mutationDeleteClass.isPending ||
                mutationDeleteDomain.isPending ||
                mutationDeleteTerminal.isPending ||
                mutationDeleteMontant.isPending
                  ? "Suppression en cours..."
                  : "Confirmer la suppression"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate dialog */}
      {state_actions.actionType === "activate" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`${
              isDarkMode ? "bg-[#1c2128]" : "bg-white"
            } rounded-lg shadow-xl w-full max-w-md mx-4 p-6`}
          >
            <div className="text-xl font-medium mb-4">
              Confirmer l'activation
            </div>
            <p
              className={`${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              } mb-6`}
            >
              {state_actions.actionText}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                disabled={
                  mutationActivateShoolYear.isPending ||
                  mutationActivateClass.isPending
                }
                onClick={() =>
                  dispatch({
                    type: "CANCEL",
                    payload: {
                      actionText: "",
                      currentId: "",
                      actionSection: "",
                    },
                  })
                }
                className={`px-4 py-2 rounded-md ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                } !rounded-button whitespace-nowrap ${
                  mutationActivateShoolYear.isPending ||
                  mutationActivateClass.isPending
                    ? "cursor-not-allowed"
                    : ""
                }`}
              >
                Annuler
              </button>
              <button
                disabled={
                  mutationActivateShoolYear.isPending ||
                  mutationActivateClass.isPending
                }
                onClick={handleActivate}
                className={`px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white !rounded-button whitespace-nowrap ${
                  mutationActivateShoolYear.isPending ||
                  mutationActivateClass.isPending
                    ? "cursor-not-allowed"
                    : ""
                }`}
              >
                {mutationActivateShoolYear.isPending ||
                mutationActivateClass.isPending
                  ? "Activation en cours..."
                  : "Confirmer l'activation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desactivate dialog */}
      {state_actions.actionType === "desactivate" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`${
              isDarkMode ? "bg-[#1c2128]" : "bg-white"
            } rounded-lg shadow-xl w-full max-w-md mx-4 p-6`}
          >
            <div className="text-xl font-medium mb-4">
              Confirmer la désactivation
            </div>
            <p
              className={`${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              } mb-6`}
            >
              {state_actions.actionText}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                disabled={mutationDesactivateClass.isPending}
                onClick={() =>
                  dispatch({
                    type: "CANCEL",
                    payload: {
                      actionText: "",
                      currentId: "",
                      actionSection: "",
                    },
                  })
                }
                className={`px-4 py-2 rounded-md ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                } !rounded-button whitespace-nowrap ${
                  mutationDesactivateClass.isPending ? "cursor-not-allowed" : ""
                }`}
              >
                Annuler
              </button>
              <button
                disabled={mutationDesactivateClass.isPending}
                onClick={handleDesactivate}
                className={`px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 text-white !rounded-button whitespace-nowrap ${
                  mutationDesactivateClass.isPending ? "cursor-not-allowed" : ""
                }`}
              >
                {mutationDesactivateClass.isPending
                  ? "Activation en cours..."
                  : "Confirmer la désactivation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== NOTIFICATION ===== */}
      {notification && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white shadow-lg transition-all duration-500 ${
            notification.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default Scolarite;
